<template>
  <div class="cal">
    <div class="cal-toolbar">
      <div class="cal-nav">
        <button class="cal-navbtn" aria-label="Previous month" @click="prev">‹</button>
        <span class="cal-month">{{ monthLabel }}</span>
        <button class="cal-navbtn" aria-label="Next month" @click="next">›</button>
      </div>
      <button class="cal-today-btn" @click="today">Today</button>
    </div>

    <p v-if="loading" class="cal-muted">Loading calendar…</p>
    <p v-else-if="!posts.length" class="cal-muted">No scheduled posts yet — approve the schedule to see it here.</p>

    <div v-else class="cal-grid" role="grid" aria-label="Scheduled posts calendar">
      <div v-for="d in dow" :key="d" class="cal-dow">{{ d }}</div>
      <template v-for="(week, wi) in weeks" :key="wi">
        <div
          v-for="day in week"
          :key="day.toISOString()"
          class="cal-cell"
          :class="{ 'cal-out': day.getMonth() !== cursor.getMonth(), 'cal-today': ymd(day) === todayKey }"
        >
          <div class="cal-daynum">{{ day.getDate() }}</div>
          <div class="cal-chips">
            <button
              v-for="p in (byDay.get(ymd(day)) || [])"
              :key="p.id"
              class="cal-chip"
              :data-status="p.status"
              :title="`${p.channel} · ${timeOf(p.scheduled_at)} — ${p.campaign_topic}`"
              @click="$emit('select', p)"
            >
              <span class="cal-chip-dot" :data-status="p.status"></span>
              {{ p.channel }}
            </button>
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  posts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})
defineEmits(['select'])

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const ymd = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Default to the month of the earliest scheduled post, else this month.
const initial = props.posts.length ? new Date(props.posts[0].scheduled_at) : new Date()
const cursor = ref(startOfMonth(initial))

const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const todayKey = ymd(new Date())

const monthLabel = computed(() => cursor.value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }))

const byDay = computed(() => {
  const m = new Map()
  for (const p of props.posts) {
    if (!p.scheduled_at) continue
    const k = ymd(new Date(p.scheduled_at))
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(p)
  }
  return m
})

const weeks = computed(() => {
  const first = startOfMonth(cursor.value)
  const gridStart = new Date(first)
  gridStart.setDate(first.getDate() - first.getDay())
  const cells = []
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    cells.push(d)
  }
  return [0, 1, 2, 3, 4, 5].map((r) => cells.slice(r * 7, r * 7 + 7))
})

const prev = () => { cursor.value = new Date(cursor.value.getFullYear(), cursor.value.getMonth() - 1, 1) }
const next = () => { cursor.value = new Date(cursor.value.getFullYear(), cursor.value.getMonth() + 1, 1) }
const today = () => { cursor.value = startOfMonth(new Date()) }
const timeOf = (v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
const fmt = (v) => new Date(v).toLocaleString()
</script>

<style scoped>
.cal { color: var(--text-primary); }
.cal-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.cal-nav { display: flex; align-items: center; gap: 12px; }
.cal-month { font-weight: 700; font-size: 1.05rem; min-width: 160px; text-align: center; }
.cal-navbtn {
  width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--glass-bg);
  border: var(--glass-border); color: var(--text-primary); cursor: pointer; font-size: 1rem; line-height: 1;
}
.cal-navbtn:hover { background: var(--glass-bg-hover); }
.cal-today-btn {
  padding: 6px 12px; border-radius: var(--radius-sm); background: var(--glass-bg); border: var(--glass-border);
  color: var(--text-primary); font-size: 0.8rem; cursor: pointer;
}
.cal-today-btn:hover { background: var(--glass-bg-hover); }
.cal-muted { color: var(--text-secondary); text-align: center; padding: 40px 16px; }

.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
.cal-dow { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-secondary); text-align: center; padding-bottom: 2px; }
.cal-cell {
  min-height: 84px; border: var(--glass-border); border-radius: var(--radius-sm);
  background: var(--glass-bg); padding: 6px; display: flex; flex-direction: column; gap: 4px;
}
.cal-out { opacity: 0.4; }
.cal-today { border: var(--glass-border-hi); box-shadow: 0 0 0 1px var(--accent-soft) inset; }
.cal-daynum { font-size: 0.72rem; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.cal-today .cal-daynum { color: var(--accent-light); font-weight: 700; }
.cal-chips { display: flex; flex-direction: column; gap: 3px; }
.cal-chip {
  display: flex; align-items: center; gap: 5px; text-align: left; width: 100%;
  font-size: 0.68rem; padding: 2px 6px; border-radius: 6px; cursor: pointer;
  background: var(--glass-bg-active); border: 1px solid transparent; color: var(--text-primary);
  text-transform: capitalize; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.cal-chip:hover { border-color: var(--glass-border-hi); }
.cal-chip-dot { width: 6px; height: 6px; border-radius: 50%; flex: none; background: var(--text-muted); }
.cal-chip-wave { font-size: 0.6rem; font-weight: 700; color: var(--accent-light); flex: none; }
.cal-chip-dot[data-status='scheduled'], .cal-chip-dot[data-status='published'] { background: var(--success); }
.cal-chip-dot[data-status='failed'] { background: var(--danger); }
.cal-chip-dot[data-status='draft'] { background: var(--warning); }

.cal-detail {
  margin-top: 14px; border: var(--glass-border); border-radius: var(--radius-md);
  background: var(--glass-bg-active); padding: 14px;
}
.cal-detail-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.cal-chip-strong { font-size: 0.72rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg); color: var(--accent-light); text-transform: capitalize; }
.cal-detail-angle { font-size: 0.75rem; font-style: italic; color: var(--text-secondary); }
.cal-detail-when { font-size: 0.78rem; color: var(--text-secondary); }
.cal-detail-close { margin-left: auto; background: none; border: none; color: var(--text-secondary); cursor: pointer; font-size: 0.9rem; }
.cal-pill { font-size: 0.68rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg); color: var(--text-secondary); text-transform: capitalize; }
.cal-pill[data-status='scheduled'], .cal-pill[data-status='published'] { color: var(--success); }
.cal-pill[data-status='failed'] { color: var(--danger); }
.cal-detail-topic { font-size: 0.8rem; color: var(--text-secondary); margin: 0 0 6px; }
.cal-detail-content { font-size: 0.9rem; color: var(--text-primary); line-height: 1.5; white-space: pre-wrap; margin: 0; }

@media (max-width: 680px) {
  .cal-cell { min-height: 64px; }
  .cal-chip { font-size: 0.6rem; }
}
</style>
